import { VPadStage, VPadState } from "@/interfaces/virtualPad_types"
import { PadSessionManager } from "./PadSessionManager"
import { VPPeerConnection, VPPeerConnectionHandlers } from "./VPPeerConnection"

/**
 * Lifecycle callbacks invoked by the virtual pad when
 * connection-related events occur.
 */
export interface VPadLifecycleHandlers {
  /**
   * Invoked when the remote virtual pad explicitly leaves the current session
   */
  onRemoteClose: () => void

  /**
   * Invoked when the virtual pad connection is lost or closed
   */
  onDisconnected: () => void

  /**
   * Invoked when the virtual pad stage changes
   */
  onStageChanged: (stage: VPadStage) => void

  /**
   * Invoked when the virtual pad state changes
   */
  onStateChanged: () => void
}

/**
 * Represents a virtual pad connected to the current session
 */
export class VPad {
  private readonly id: string
  private stage: VPadStage = VPadStage.NONE
  private readonly connection: VPPeerConnection
  private connected: boolean = false
  private connectedAt: number | null = null

  private readonly mapping: GamepadMappingType = 'standard'
  private static readonly STANDARD_GAMEPAD_BUTTONS = 17
  private static readonly STANDARD_GAMEPAD_AXES = 4

  private buttons: GamepadButton[] = []
  private axes: number[] = []
  private timestamp: number
  private isVirtual: boolean

  /**
   * Initializes a virtual pad
   * 
   * The constructor is private to ensure that virtual pads are created
   * through the create factory method
   * 
   * @param sessionId Session identifier associated with the virtual pad
   * @param connectionId Connection identifier of the virtual pad
   * @param index Index assigned to the virtual pad
   * @param lifecycleHandlers Callbacks notified when the virtual pad leaves or becomes disconnected
   */
  private constructor(
    private readonly sessionId: string,
    private readonly connectionId: string,
    private index: number,
    private readonly lifecycleHandlers: VPadLifecycleHandlers
  ) {
    this.id = `Sorapad-${connectionId}`
    this.isVirtual = true
    this.initializeButtonsAndAxes()
    this.timestamp = 0
    this.stage = VPadStage.CONNECTING
    this.connection = new VPPeerConnection(
      sessionId,
      connectionId,
      this.createConnectionHandlers()
    )
  }

  /**
   * Initializes the virtual gamepad state
   * 
   * Creates the default buttons and axes using the standard Gamepad mapping
   */
  private initializeButtonsAndAxes (): void {
    this.buttons = Array.from(
      { length: VPad.STANDARD_GAMEPAD_BUTTONS },
      () => ({
        pressed: false,
        touched: false,
        value: 0,
      })
    )

    this.axes = Array.from(
      { length: VPad.STANDARD_GAMEPAD_AXES },
      () => 0
    )
  }

  /**
   * Creates the callbacks used by the peer connection to update
   * the virtual pad state and propagate lifecycle events
   *
   * @returns Peer connection event handlers
   */
  private createConnectionHandlers(): VPPeerConnectionHandlers {
    return {
      onButtonChanged: (buttonIndex, value) => {
        this.setButton(buttonIndex, value)
        this.handleStateChanged()
      },

      onAxisChanged: (axisIndex, value) => {
        this.setAxis(axisIndex, value)
        this.handleStateChanged()
      },

      onStatusChanged: (status) => {
        this.setStage(status)
        this.handleStageChanged(status)
      },

      onRemoteClose: () => {
        this.handleRemoteClose()
      },

      onDisconnected: () => {
        this.handleDisconnected()
      },
    }
  }

  /**
   * Creates a new virtual pad
   * 
   * Validates the provided parameters and returns a virtual pad instance
   * in the connecting state. The WebRTC connection is not established
   * until connect() is called.
   * 
   * @param sessionId Session identifier associated with the virtual pad
   * @param connectionId Connection identifier of the virtual pad
   * @param index Index assigned to the virtual pad
   * @param lifecycleHandlers Callbacks notified when the virtual pad leaves or becomes disconnected
   * @returns The created virtual pad
   * @throws If the provided parameters are invalid
   */
  public static create (
    sessionId: string,
    connectionId: string,
    index: number,
    lifecycleHandlers: VPadLifecycleHandlers
  ): VPad {
    VPad.validateCreationParams(sessionId, connectionId)
    return new VPad(sessionId, connectionId, index, lifecycleHandlers)
  }

  /**
   * Validates the parameters required to create a virtual pad.
   *
   * @param sessionId Session identifier
   * @param connectionId Connection identifier
   * @throws If any parameter is invalid
   */
  private static validateCreationParams(sessionId: string, connectionId: string): void {
    if (sessionId.trim() === '')
      throw new Error(`VPAD :: CREATE :: The session ID can't be empty`)

    if (connectionId.trim() === '')
      throw new Error(`VPAD :: CREATE :: The connection ID can't be empty`)

    const currentSessionId = PadSessionManager.getInstance().getSessionId()

    if (currentSessionId === null || sessionId !== currentSessionId)
      throw new Error(`VPAD :: CREATE :: The session ID doesn't match the current session`)
  }

  /**
   * Establishes the WebRTC connection for the virtual pad
   * 
   * Waits until the WebRTC connection has been successfully established
   * and updates the virtual pad state accordingly
   * 
   * @throws If the virtual pad is not in the connecting state or the WebRTC connection cannot be established
   */
  public async connect(): Promise<void> {
    if (this.stage !== VPadStage.CONNECTING)
      throw new Error(`VPAD :: CONNECT :: The virtual pad isn't in connecting state`)

    try {
      await this.connection.connect()

      if (this.stage !== VPadStage.CONNECTING) {
        this.connection.close()
        return
      }

      this.stage = VPadStage.CONNECTED
      this.setConnected(true)
    } catch (error) {
      this.close()
      throw error
    }
  }

  /**
   * Applies the WebRTC answer received from the virtual pad
   *
   * @param sdp Remote answer SDP
   */
  public async setAnswer(sdp: string): Promise<void> {
    await this.connection.setAnswer(sdp)
  }

  /**
   * Adds an ICE candidate received from the virtual pad
   *
   * @param candidate Remote ICE candidate
   */
  public async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    await this.connection.addIceCandidate(candidate)
  }


  /**
   * Notifies the remote virtual pad that it has been ejected by the host
   *
   * If the peer connection is not currently established, no notification can be sent
   */
  public requestEject(): void {
    if (!this.connection.isConnected()) return

    this.connection.sendEject()
  }

  /**
   * Notifies the remote virtual pad that it has been manually
   * removed by the host.
   *
   * If the peer connection is not currently established,
   * no notification can be sent.
   */
  public requestLeave(): void {
    // console.warn(`VPAD ${this.connectionId} :: REQUEST LEAVE`)
    if (!this.connection.isConnected()) return

    this.connection.sendLeave()
  }

  /**
   * Closes the virtual pad WebRTC connection
   */
  public close(): void {
    if (this.stage === VPadStage.DISCONNECTED) return

    this.stage = VPadStage.DISCONNECTED
    this.setConnected(false)
    this.connection.close()
  }

  /**
   * Handles a remote close request received from the remote virtual pad
   * 
   * The event is propagated to the lifecycle owner so it can
   * remove and dispose of this virtual pad
   */
  private handleRemoteClose(): void {
    this.lifecycleHandlers.onRemoteClose()
  }

  /**
   * Handles the disconnection of the virtual pad
   * 
   * The event is propagated to the lifecycle owner so it can
   * remove and dispose of this virtual pad
   */
  private handleDisconnected(): void {
    this.lifecycleHandlers.onDisconnected()
  }

  /**
   * Handles the stage change of the virtual pad
   *
   * The event is propagated to the lifecycle owner so it can
   * operate on VPad stage changes
   */
  private handleStageChanged(stage: VPadStage): void {
    this.lifecycleHandlers.onStageChanged(stage)
  }

  /**
   * Handles the change of state of the virtual pad
   * 
   * The event is propagated to the lifecycle owner so it can
   * operate on VPad state changes
   */
  private handleStateChanged(): void {
    this.lifecycleHandlers.onStateChanged()
  }

  /**
   * Returns the session identifier associated with the virtual pad
   *
   * @returns The session identifier
   */
  public getSessionId(): string {
    return this.sessionId
  }

  /**
   * Returns the connection identifier of the virtual pad
   *
   * @returns The connection identifier
   */
  public getConnectionId(): string {
    return this.connectionId
  }

  /**
   * Returns the identifier assigned to the virtual pad
   *
   * @returns The virtual pad identifier
   */
  public getId(): string {
    return this.id
  }

  /**
   * Returns the current virtual pad stage
   *
   * @returns The current stage
   */
  public getStage(): VPadStage {
    return this.stage
  }

  /**
   * Returns the current connected value assigned to the virtual pad
   * 
   * @returns The current connected
   */
  public getConnected(): boolean {
    return this.connected
  }

  /**
   * Returns the timestamp at which the virtual pad was connected
   * 
   * @returns The current connectedAt
   */
  public getConnectedAt(): number | null {
    return this.connectedAt
  }

  /**
   * Returns the current index assigned to the virtual pad
   * 
   * @returns The current index
   */
  public getIndex(): number {
    return this.index
  }

  /**
   * Returns the current mapping assigned to the virtual pad
   * 
   * @returns The current mapping
   */
  public getMapping(): GamepadMappingType {
    return this.mapping
  }

  /**
   * Returns the current buttons state
   *
   * @returns A copy of the virtual gamepad buttons
   */
  public getButtons(): GamepadButton[] {
    return this.buttons.map((button) => ({
      ...button,
    }))
  }

  /**
   * Returns the current axes state
   *
   * @returns A copy of the virtual gamepad axes
   */
  public getAxes(): number[] {
    return [...this.axes]
  }

  /**
   * Returns the timestamp of the last state update
   * 
   * The timestamp is refreshed whenever the virtual gamepad state changes
   * 
   * @returns The last update timestamp
   */
  public getTimestamp(): number {
    return this.timestamp
  }

  /**
   * Returns the current virtual pad state
   *
   * @returns A serializable copy of the virtual pad state
   */
  public getVPadState(): VPadState {
    return {
      id: this.id,
      connectionId: this.connectionId,
      index: this.index,
      isVirtual: this.isVirtual,
      connected: this.connected,
      stage: this.stage,
      mapping: this.mapping,
      timestamp: this.timestamp,
      buttons: this.buttons.map((button) => ({
        ...button,
      })),
      axes: [...this.axes]
    }
  }

  /**
   * Updates the virtual gamepad timestamp
   * 
   * This should be called whenever the gamepad state changes
   */
  private updateTimestamp(): void {
    this.timestamp = performance.now()
  }

  /**
   * Changes the index assigned to the virtual pad
   * 
   * @param index New index assigned to the virtual pad
   */
  public setIndex(index: number): void {
    if (!Number.isInteger(index) || index < 0)
      throw new Error(`VPAD :: SET INDEX :: The index must be a non-negative integer`)

    this.index = index
    this.updateTimestamp()
  }

  /**
   * Changes the connected assigned to the virtual pad
   * 
   * @param connected New connected value assigned to the virtual pad
   */
  private setConnected(connected: boolean): void {
    this.connected = connected
    this.connectedAt = connected ? Date.now() : null
    this.updateTimestamp()
  }

  /**
   * Changes the stage assigned to the virtual pad
   *
   * @param stage New stage assigned to the virtual pad
   */
  private setStage(stage: VPadStage): void {
    this.stage = stage
    this.updateTimestamp()
  }

  /**
   * Updates the state of a gamepad button
   * 
   * @param buttonIndex Index of the button to update
   * @param value Button value in the range [0, 1]
   * @param touched Indicates whether the button is being touched
   */
  public setButton(
    buttonIndex: number,
    value: number,
    touched = value > 0
  ): void {
    const button = this.buttons[buttonIndex]

    if (!button) throw new Error(`VPAD :: SET BUTTON :: Unknown gamepad button index: ${buttonIndex}`)

    const normalizedValue = Math.min(1, Math.max(0, value))

    this.buttons[buttonIndex] = {
      pressed: normalizedValue > 0.1,
      touched,
      value: normalizedValue,
    }

    this.updateTimestamp()
  }

  /**
   * Updates the value of a gamepad axis
   * 
   * @param axisIndex Index of the axis to update
   * @param value Axis value in the range [-1, 1]
   */
  public setAxis(axisIndex: number, value: number): void {
    if (this.axes[axisIndex] === undefined)
      throw new Error(`VPAD :: SET AXIS :: Unknown gamepad axis index: ${axisIndex}`)

    this.axes[axisIndex] = Math.min(1, Math.max(-1, value))
    this.updateTimestamp()
  }
}